import React, { useState, useRef, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { GoogleMap, useJsApiLoader, TransitLayer, Polyline, Marker, InfoWindow, Circle } from "@react-google-maps/api";
import { getData, postDataWithBody } from "../../../Services/Services";
import { getVehicleRouteAlertTrackWindow, getVehicleRouteAlertTrackWithAlerts, getWayPoints } from "../../../Services/EndPoints";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
} from "../GpsTrackManagement/Googlemapsloader";

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const DEFAULT_CENTER = { lat: 17.4435, lng: 78.3485 };

const LAYERS = [
  {
    id: "roadmap", label: "Standard",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
  },
  {
    id: "satellite", label: "Satellite",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  },
  {
    id: "terrain", label: "Terrain",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 18 9 6 13 13 16 9 21 18"/></svg>,
  },
  {
    id: "hybrid", label: "Hybrid",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
  },
];

const CustomLayerControl = ({ mapType, setMapType }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          width: 40, height: 40, border: "none", background: "white",
          borderRadius: 8, cursor: "pointer", display: "flex",
          alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2"/>
          <polyline points="2 17 12 22 22 17"/>
          <polyline points="2 12 12 17 22 12"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: 45, right: 0,
          background: "white", borderRadius: 10, padding: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.14)", minWidth: 160,
          border: "1px solid #e2e8f0",
        }}>
          {LAYERS.map((opt) => (
            <div
              key={opt.id}
              onClick={() => { setMapType(opt.id); setOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 9,
                padding: "8px 10px", cursor: "pointer", borderRadius: 7,
                background: mapType === opt.id ? "#f0fdfb" : "transparent",
                transition: "background 0.15s",
              }}
            >
              <span style={{ color: mapType === opt.id ? "#1a7a73" : "#94a3b8" }}>{opt.icon}</span>
              <span style={{
                fontSize: 13,
                fontWeight: mapType === opt.id ? 600 : 400,
                color: mapType === opt.id ? "#1a7a73" : "#334155",
              }}>{opt.label}</span>
              {mapType === opt.id && (
                <span style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: "#1a7a73" }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TransitToggleControl = ({ showTransit, setShowTransit }) => (
  <button
    onClick={() => setShowTransit((p) => !p)}
    title={showTransit ? "Hide transit" : "Show transit"}
    style={{
      width: 40, height: 40, border: "none",
      background: showTransit ? "#1a7a73" : "white",
      borderRadius: 8, cursor: "pointer", display: "flex",
      alignItems: "center", justifyContent: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    }}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke={showTransit ? "white" : "#333"} strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="16" rx="2"/>
      <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
      <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
    </svg>
  </button>
);

const InfoCard = ({ icon, iconBg, iconColor, cardBg, borderColor, accentColor, label, children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10, borderLeft: `4px solid ${accentColor}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
    <div style={{ width: 42, height: 42, borderRadius: 10, background: iconBg, color: iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {icon}
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontSize: 10, color: accentColor, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  </div>
);

const formatAlertDateTime = (iso) => {
  if (!iso) return "N/A";
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return iso;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day}-${month}-${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  } catch {
    return iso;
  }
};

// trackingData entries can carry their timestamp either as an ISO string or as a
// Java LocalDateTime array [year, month(1-based), day, hour, minute, second, nanos] —
// resolve whichever shape shows up under whichever time-ish field name is present,
// rather than assuming one fixed field name/format.
const trackingTimeValue = (point) => {
  const timeKey = Object.keys(point || {}).find((k) => /time$/i.test(k) || k === "createdOn");
  const raw = timeKey ? point[timeKey] : null;
  if (!raw) return 0;
  if (Array.isArray(raw)) {
    const [year, month = 1, day = 1, hour = 0, minute = 0, second = 0] = raw;
    return new Date(year, month - 1, day, hour, minute, second).getTime();
  }
  const parsed = new Date(raw).getTime();
  return isNaN(parsed) ? 0 : parsed;
};

// Roads API snapToRoads — snaps each raw GPS point onto the road it was actually
// on, respecting travel direction, so the "actual travelled path" line follows the
// street instead of drawing straight chords between noisy GPS fixes. Falls back to
// DirectionsService routed segments if the Roads API isn't enabled on the key.
// Ported as-is from the GPS Tracking screen's snapPointsToRoad (GpsTrackViewDetails.js)
// so both screens road-snap identically.
const snapPointsToRoad = async (points) => {
  const valid = points.filter((p) => {
    const la = parseFloat(p.lat), lo = parseFloat(p.lng);
    return !isNaN(la) && !isNaN(lo) && la >= -90 && la <= 90 && lo >= -180 && lo <= 180;
  });
  if (valid.length < 2) return points.map((p) => [p.lat || 0, p.lng || 0]);

  // ── Pre-processing: speed-aware noise reduction ───────────────────────────
  const toRad = (d) => (d * Math.PI) / 180;
  const haversineM = (a, b) => {
    const dLat = toRad(parseFloat(b.lat) - parseFloat(a.lat));
    const dLng = toRad(parseFloat(b.lng) - parseFloat(a.lng));
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(parseFloat(a.lat))) * Math.cos(toRad(parseFloat(b.lat))) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371000 * Math.asin(Math.sqrt(Math.min(1, h)));
  };

  // 1. Speed-aware deduplication:
  //    Moving (speed > 2 km/h) → always keep, no matter how close — Roads API needs
  //    every direction hint to pick the right road in urban areas.
  //    Stationary (speed ≈ 0) → keep only if > 5 m from last kept point to avoid
  //    flooding the batch with GPS chatter from long traffic stops.
  const deduped = [valid[0]];
  for (let i = 1; i < valid.length; i++) {
    const curr = valid[i];
    const isStationary = (parseFloat(curr.speed) || 0) < 2;
    if (!isStationary || haversineM(deduped[deduped.length - 1], curr) >= 5) {
      deduped.push(curr);
    }
  }
  if (deduped[deduped.length - 1] !== valid[valid.length - 1]) deduped.push(valid[valid.length - 1]);

  // 2. Remove isolated GPS teleport jumps (> 500 m from BOTH neighbours)
  const cleaned = deduped.filter((p, i) => {
    if (i === 0 || i === deduped.length - 1) return true;
    const dPrev = haversineM(deduped[i - 1], p);
    const dNext = haversineM(p, deduped[i + 1]);
    if (dPrev > 500 && dNext > 500) return false;
    return true;
  });

  let pts = cleaned.length >= 2 ? cleaned : valid;

  // ── Gap densification ─────────────────────────────────────────────────────
  // Roads API interpolation breaks down when consecutive GPS points are > 300 m
  // apart (sparse highway / tunnel / signal loss). For those specific gaps, use
  // Directions API — which follows road geometry between any two points — to
  // insert intermediate road points before snapping.
  const GAP_M = 300;
  if (pts.some((p, i) => i > 0 && haversineM(pts[i - 1], p) > GAP_M) && window.google?.maps?.DirectionsService) {
    const densified = [pts[0]];
    for (let i = 1; i < pts.length; i++) {
      const gap = haversineM(pts[i - 1], pts[i]);
      if (gap > GAP_M) {
        if (densified.length > 1) await new Promise((r) => setTimeout(r, 100));
        const a = pts[i - 1], b = pts[i];
        const midPts = await new Promise((resolve) => {
          new window.google.maps.DirectionsService().route(
            {
              origin: new window.google.maps.LatLng(parseFloat(a.lat), parseFloat(a.lng)),
              destination: new window.google.maps.LatLng(parseFloat(b.lat), parseFloat(b.lng)),
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (status === window.google.maps.DirectionsStatus.OK && result.routes[0]) {
                const path = [];
                result.routes[0].legs.forEach((leg) =>
                  leg.steps.forEach((step) =>
                    step.path.forEach((pt) =>
                      path.push({ lat: pt.lat(), lng: pt.lng(), speed: ((parseFloat(a.speed) || 0) + (parseFloat(b.speed) || 0)) / 2 })
                    )
                  )
                );
                resolve(path.slice(1, -1)); // exclude endpoints — already in densified
              } else {
                resolve([]);
              }
            }
          );
        });
        densified.push(...midPts);
      }
      densified.push(pts[i]);
    }
    pts = densified.length >= 2 ? densified : pts;
  }

  // ── Turn detection ────────────────────────────────────────────────────────
  // Smoothed bearing uses a ±2 point window to avoid GPS noise at low speed.
  // A real turn = bearing shift > 45° where the vehicle is moving (> 5 km/h).
  const calcBearing = (a, b) => {
    const la1 = toRad(parseFloat(a.lat)), la2 = toRad(parseFloat(b.lat));
    const dLo = toRad(parseFloat(b.lng) - parseFloat(a.lng));
    return ((Math.atan2(Math.sin(dLo) * Math.cos(la2), Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLo)) * 180) / Math.PI + 360) % 360;
  };
  const angleDiff = (b1, b2) => { const d = Math.abs(b1 - b2) % 360; return d > 180 ? 360 - d : d; };
  const smoothBearing = (i) => calcBearing(pts[Math.max(0, i - 2)], pts[Math.min(pts.length - 1, i + 2)]);

  const turnSet = new Set([0, pts.length - 1]);
  for (let i = 1; i < pts.length - 1; i++) {
    if ((parseFloat(pts[i].speed) || 0) < 5) continue;
    if (angleDiff(smoothBearing(i - 1), smoothBearing(i + 1)) > 45) turnSet.add(i);
  }

  // ── Turn-aware batching ────────────────────────────────────────────────────
  // End each batch at a detected turn so the Roads API always starts a new
  // batch from a clean anchor on the correct road branch — preventing the API
  // from interpolating across a fork or taking the wrong parallel road.
  // Falls back to a fixed-size cut (with 5-pt overlap) when no turn is found.
  const MAX_B = 93;
  const SZ_OVL = 5;
  const batches = [];
  let bStart = 0;

  while (bStart < pts.length - 1) {
    const limit = bStart + MAX_B;
    let bEnd = pts.length - 1;
    let turnCut = true;

    if (limit < pts.length - 1) {
      let found = false;
      for (let j = Math.min(limit, pts.length - 2); j > bStart + Math.floor(MAX_B / 2); j--) {
        if (turnSet.has(j)) { bEnd = j; found = true; break; }
      }
      if (!found) { bEnd = limit; turnCut = false; }
    }

    batches.push({ slice: pts.slice(bStart, bEnd + 1), turnCut });
    bStart = turnCut ? bEnd : Math.max(bEnd - SZ_OVL + 1, bStart + 1);
  }

  // ── Roads API snapToRoads ─────────────────────────────────────────────────
  const snapped = [];
  let roadsOk = false;

  for (let b = 0; b < batches.length; b++) {
    if (b > 0) await new Promise((r) => setTimeout(r, 150));
    const { slice: batch, turnCut } = batches[b];
    const path = batch.map((p) => `${parseFloat(p.lat).toFixed(7)},${parseFloat(p.lng).toFixed(7)}`).join("|");

    try {
      const res = await fetch(
        `https://roads.googleapis.com/v1/snapToRoads?path=${path}&interpolate=true&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await res.json();

      if (data.error) { console.warn("Roads API error:", data.error.message); break; }

      if (data.snappedPoints?.length > 0) {
        roadsOk = true;
        if (b === 0) {
          snapped.push(...data.snappedPoints.map((p) => [p.location.latitude, p.location.longitude]));
        } else {
          // Turn cut → 1 shared anchor point → skip snapped output for originalIndex 0
          // Size cut → SZ_OVL shared points → skip snapped output for originalIndex < SZ_OVL
          const skipTo = turnCut ? 1 : SZ_OVL;
          const firstNew = data.snappedPoints.findIndex((p) => p.originalIndex !== undefined && p.originalIndex >= skipTo);
          const toAdd = firstNew >= 0 ? data.snappedPoints.slice(firstNew) : data.snappedPoints;
          snapped.push(...toAdd.map((p) => [p.location.latitude, p.location.longitude]));
        }
      }
    } catch (err) {
      console.warn("Roads API batch failed:", err);
      break;
    }
  }

  if (roadsOk && snapped.length > pts.length) return snapped;

  // ── Fallback: DirectionsService in short segments ─────────────────────────
  const resample = (arr, n) => {
    if (arr.length <= n) return arr;
    const out = [], step = (arr.length - 1) / (n - 1);
    for (let j = 0; j < n; j++) out.push(arr[Math.min(Math.round(j * step), arr.length - 1)]);
    out[out.length - 1] = arr[arr.length - 1];
    return out;
  };

  const routeSegment = (segPts) => new Promise((resolve) => {
    if (!window.google?.maps?.DirectionsService) {
      resolve({ path: segPts.map((p) => [p.lat, p.lng]), ok: false }); return;
    }
    const sel = resample(segPts, 25);
    const orig = new window.google.maps.LatLng(sel[0].lat, sel[0].lng);
    const dest = new window.google.maps.LatLng(sel[sel.length - 1].lat, sel[sel.length - 1].lng);
    const wpts = sel.slice(1, -1).map((p) => ({ location: new window.google.maps.LatLng(p.lat, p.lng), stopover: false }));
    new window.google.maps.DirectionsService().route(
      { origin: orig, destination: dest, waypoints: wpts, travelMode: window.google.maps.TravelMode.DRIVING, optimizeWaypoints: false },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK && result.routes[0]) {
          const p = [];
          result.routes[0].legs.forEach((leg) => leg.steps.forEach((step) => step.path.forEach((pt) => p.push([pt.lat(), pt.lng()]))));
          resolve({ path: p, ok: true });
        } else {
          resolve({ path: segPts.map((pp) => [pp.lat, pp.lng]), ok: false });
        }
      }
    );
  });

  const NUM_SEGS = pts.length > 150 ? 6 : pts.length > 75 ? 3 : 1;
  const SEG_SIZE = Math.ceil(pts.length / NUM_SEGS);
  const fullPath = [];
  let dsOk = false;

  for (let s = 0; s < NUM_SEGS; s++) {
    if (s > 0) await new Promise((r) => setTimeout(r, 200));
    const start = s * SEG_SIZE;
    const end = Math.min(pts.length, start + SEG_SIZE + 1);
    const { path, ok } = await routeSegment(pts.slice(start, end));
    if (ok) dsOk = true;
    fullPath.push(...(s === 0 ? path : path.slice(1)));
  }

  return dsOk && fullPath.length > pts.length ? fullPath : pts.map((p) => [p.lat, p.lng]);
};

// Same waypoint pin/halt icons + popup used in the Planned Route screen —
// keeps the deviation map visually consistent with the assigned-route view.
const HALT_POINT_COLORS = { start: "#22c55e", halt: "#f59e0b", stop: "#f59e0b", end: "#ef4444" };

const makePinSvg = (fillColor) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="44" viewBox="0 0 28 44">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 5.817 3.412 10.83 8.35 13.269L14 44l5.65-16.731C24.588 24.83 28 19.817 28 14 28 6.268 21.732 0 14 0z"
      fill="${fillColor}" stroke="white" stroke-width="1.5"/>
    <circle cx="14" cy="14" r="5.5" fill="white" fill-opacity="0.85"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const makeHaltSvg = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
    <circle cx="17" cy="17" r="16" fill="#FBBC04" stroke="white" stroke-width="2.5"/>
    <rect x="11" y="10" width="4" height="14" rx="1.5" fill="white"/>
    <rect x="19" y="10" width="4" height="14" rx="1.5" fill="white"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const createCustomIcon = (type) => {
  if (!window.google) return undefined;
  const t = (type || "").toLowerCase();
  if (t === "halt" || t === "stop") {
    return { url: makeHaltSvg(), scaledSize: new window.google.maps.Size(34, 34), anchor: new window.google.maps.Point(17, 17) };
  }
  const color = t === "start" ? "#22c55e" : "#ef4444";
  return { url: makePinSvg(color), scaledSize: new window.google.maps.Size(28, 44), anchor: new window.google.maps.Point(14, 44) };
};

// Suppress native InfoWindow chrome so only our custom-styled popup shows
const infoWindowStyle = `
  .gm-ui-hover-effect { display: none !important; }
  .gm-style-iw-chr   { display: none !important; }
  .gm-style-iw-d     { overflow: hidden !important; padding: 0 !important; }
  .gm-style-iw-c     { padding: 0 !important; border-radius: 8px !important;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.2) !important; }
  .gm-style-iw       { padding: 0 !important; border-radius: 8px !important; }
`;

const MarkerPopup = ({ title, titleColor, onClose, rows, address }) => {
  const isHalt  = title.toLowerCase().includes("halt");
  const isStart = title.toLowerCase().includes("start");
  const isEnd   = title.toLowerCase().includes("end");
  const gradMap = {
    "#22c55e": "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    "#f59e0b": "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    "#ef4444": "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
  };
  const tintMap = {
    "#22c55e": "#f0fdf4",
    "#f59e0b": "#fffbeb",
    "#ef4444": "#fef2f2",
  };
  const hdrGrad = gradMap[titleColor] || `linear-gradient(135deg, ${titleColor} 0%, ${titleColor} 100%)`;
  const tintBg  = tintMap[titleColor] || "#f8fafc";
  const subtitle = isStart ? "Assigned starting location" : isEnd ? "Assigned ending location" : "Scheduled stop location";
  return (
    <div style={{ minWidth: 260, maxWidth: 300, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
      <div style={{ background: hdrGrad, padding: "12px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {isHalt
            ? <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16" rx="2"/><rect x="14" y="4" width="4" height="16" rx="2"/></svg>
            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white" stroke="none"/></svg>
          }
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{title}</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>{subtitle}</div>
        </div>
        <button onClick={onClose} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
      </div>
      <div style={{ padding: "12px 14px 14px", background: "#fff" }}>
        {rows.map((r, i) => (
          <div key={r.label} style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: tintBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={titleColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
            <div>
              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>{r.label}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{r.value || "—"}</div>
            </div>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
          <div style={{ width: 26, height: 26, borderRadius: 6, background: tintBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={titleColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Location</div>
            <div style={{ fontSize: 11, color: "#475569", background: tintBg, borderRadius: 6, padding: "6px 10px", borderLeft: `3px solid ${titleColor}`, lineHeight: 1.5 }}>
              {address
                ? address
                : <><span className="spinner-border spinner-border-sm me-2" style={{ width: 10, height: 10 }} />Loading address…</>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const waypointTitle = (pointType) => {
  const t = (pointType || "").toLowerCase();
  if (t === "start") return "Start Point";
  if (t === "end") return "End Point";
  return "Halt Point";
};
const waypointShortLabel = (pointType) => {
  const t = (pointType || "").toLowerCase();
  if (t === "start") return "S";
  if (t === "end") return "E";
  return "H";
};

// ─── Deviation alert marker (track-with-alerts) ───────────────────────────────
// Each alert type (HALT_EXCEEDED, ROUTE_DEVIATION, HALT_MISSED, EXTRA_HALT) carries
// its lat/lng inside a differently-named "*Details" object — pull it out generically
// instead of hardcoding a field name per alert type we haven't seen a sample of yet.
const extractAlertLatLng = (alertObj) => {
  const detailsKey = Object.keys(alertObj).find(
    (k) => k.toLowerCase().endsWith("details") && alertObj[k] && typeof alertObj[k] === "object"
  );
  if (!detailsKey) return null;
  const details = alertObj[detailsKey];
  const latKey = Object.keys(details).find((k) => /(latitude|lat)$/i.test(k));
  const lngKey = Object.keys(details).find((k) => /(longitude|lng|long)$/i.test(k));
  if (!latKey || !lngKey) return null;
  const lat = parseFloat(details[latKey]);
  const lng = parseFloat(details[lngKey]);
  if (isNaN(lat) || isNaN(lng)) return null;
  return { lat, lng, details, latKey, lngKey };
};

const formatDetailLabel = (key) =>
  key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).trim();

const formatAlertTypeLabel = (type) =>
  (type || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const buildAlertHighlightIcon = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <circle cx="20" cy="20" r="17" fill="#dc2626" stroke="white" stroke-width="3"/>
    <text x="20" y="27" text-anchor="middle" font-size="20" font-weight="900" font-family="Arial,sans-serif" fill="white">!</text>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(40, 40),
    anchor: new window.google.maps.Point(20, 20),
  };
};

const DeviationAlertPopup = ({ alert: a, onClose, address }) => {
  const detailRows = Object.entries(a.details || {})
    .filter(([k]) => !/(latitude|lat|longitude|lng|long)$/i.test(k))
    .map(([k, v]) => ({
      label: formatDetailLabel(k),
      value: /(time|date)$/i.test(k) && typeof v === "string" ? formatAlertDateTime(v) : v,
    }));

  return (
    <div style={{ minWidth: 270, maxWidth: 310, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
      <div style={{ background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)", padding: "12px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{formatAlertTypeLabel(a.alertType)}</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.alertRefId || "Deviation Alert"}</div>
        </div>
        <button onClick={onClose} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
      </div>
      <div style={{ padding: "12px 14px 14px", background: "#fff", maxHeight: 300, overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
          <div style={{ width: 26, height: 26, borderRadius: 6, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Alert Time</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{formatAlertDateTime(a.alertTime)}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
          <div style={{ width: 26, height: 26, borderRadius: 6, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Status</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{a.isAcknowledged ? "Acknowledged" : "Pending"} · {a.routeStatus || "—"}</div>
          </div>
        </div>
        {detailRows.map((r) => (
          <div key={r.label} style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
            <div>
              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>{r.label}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{r.value ?? "—"}</div>
            </div>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
          <div style={{ width: 26, height: 26, borderRadius: 6, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Location</div>
            <div style={{ fontSize: 11, color: "#475569", background: "#fef2f2", borderRadius: 6, padding: "6px 10px", borderLeft: "3px solid #dc2626", lineHeight: 1.5 }}>
              {address
                ? address
                : <><span className="spinner-border spinner-border-sm me-2" style={{ width: 10, height: 10 }} />Loading address…</>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
// Draws the alert's assigned route (green, with start/halt/end waypoints), the
// vehicle's actual travelled path (red), and highlights the deviation alert
// point(s) with a popup of alert-specific details.
const DeviationRoute = () => {
  const location = useLocation();
  const alert = location.state || {};

  const [mapType, setMapType] = useState("roadmap");
  const [showTransit, setShowTransit] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapCardRef = useRef(null);
  const mapRef = useRef(null);

  const [routeInfo, setRouteInfo] = useState(null);       // raw track-window response.data
  const [haltPoints, setHaltPoints] = useState([]);        // start/halt/end points for markers
  const [routeCoordinates, setRouteCoordinates] = useState([]); // full sorted waypoint path
  const [deviationAlerts, setDeviationAlerts] = useState([]); // track-with-alerts: highlighted alert points
  const [actualTrackCoordinates, setActualTrackCoordinates] = useState([]); // track-with-alerts: actual travelled path
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [routeError, setRouteError] = useState("");
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [keyPointsAddresses, setKeyPointsAddresses] = useState({});
  const geocoderRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (isLoaded && window.google && !geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  // ── Reverse geocode a waypoint's lat/lng into a readable address ────────────
  const reverseGeocode = useCallback((lat, lng) =>
    new Promise((resolve) => {
      if (!geocoderRef.current) { resolve(null); return; }
      geocoderRef.current.geocode(
        { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const comps = results[0].address_components;
            const get = (...types) => {
              for (const t of types) { const c = comps.find((ac) => ac.types.includes(t)); if (c) return c.long_name; }
              return null;
            };
            const parts = [
              get("route", "premise", "establishment"),
              get("sublocality_level_1", "sublocality", "neighborhood"),
              get("locality", "administrative_area_level_3"),
              get("administrative_area_level_1"),
              get("postal_code"),
            ].filter(Boolean);
            resolve(parts.length ? parts.join(", ") : results[0].formatted_address);
          } else {
            resolve(null);
          }
        }
      );
    }), []);

  const geocodeKeyPoints = useCallback(async (points) => {
    const results = await Promise.allSettled(
      points.map(async (point, index) => {
        await new Promise((r) => setTimeout(r, index * 200)); // stagger to avoid rate limiting
        const address = await reverseGeocode(point.latitude, point.longitude);
        return { id: point.id, address: address || `${parseFloat(point.latitude).toFixed(6)}, ${parseFloat(point.longitude).toFixed(6)}` };
      })
    );
    const map = {};
    results.forEach((r) => { if (r.status === "fulfilled") map[r.value.id] = r.value.address; });
    setKeyPointsAddresses(map);
  }, [reverseGeocode]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      mapCardRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  const fitBoundsToRoute = useCallback((coords) => {
    if (!mapRef.current || !window.google || coords.length === 0) return;
    if (coords.length === 1) {
      mapRef.current.setCenter(coords[0]);
      mapRef.current.setZoom(15);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    coords.forEach((c) => bounds.extend(c));
    mapRef.current.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
  }, []);

  // ── Step 1: track-window → assigned-route waypoints ─────────────────────────
  useEffect(() => {
    if (!alert.vehicleTrackId) return;

    const fetchDeviationRoute = async () => {
      setIsLoadingRoute(true);
      setRouteError("");
      try {
        const trackWindowRes = await getData(getVehicleRouteAlertTrackWindow.replace("ID", alert.vehicleTrackId));
        if (!trackWindowRes?.success || !trackWindowRes?.data) {
          throw new Error(trackWindowRes?.message || "Failed to fetch track window");
        }
        const info = trackWindowRes.data;
        setRouteInfo(info);

        const routeId = info.travelRoutes?.[0]?.routeId;
        if (!info.vehicleRouteId || !routeId) {
          throw new Error("No assigned route found for this alert");
        }

        const waypointsPayload = {
          id: info.vehicleRouteId,
          vehicleNumber: info.vehicleNumber,
          routeId,
          startDate: info.startTime,
          endDate: info.endTime,
        };
        const alertsQuery = `?vehicleNumber=${encodeURIComponent(info.vehicleNumber)}&startTime=${encodeURIComponent(info.startTime)}&endTime=${encodeURIComponent(info.endTime)}&vehicleTrackId=${alert.vehicleTrackId}`;

        const [waypoints, trackWithAlertsRes] = await Promise.all([
          postDataWithBody(getWayPoints, waypointsPayload, {}),
          getData(getVehicleRouteAlertTrackWithAlerts + alertsQuery),
        ]);

        let halts = [];
        let assignedCoords = [];
        if (Array.isArray(waypoints) && waypoints.length > 0) {
          const sorted = [...waypoints].sort((a, b) => a.sequenceNo - b.sequenceNo);
          assignedCoords = sorted
            .map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }))
            .filter((c) => !isNaN(c.lat) && !isNaN(c.lng));
          setRouteCoordinates(assignedCoords);

          halts = sorted.filter((p) => ["start", "halt", "end", "stop"].includes((p.pointType || "").toLowerCase()));
          setHaltPoints(halts);
        } else {
          setRouteCoordinates([]);
          setHaltPoints([]);
          toast.warning("No waypoints found for this route");
        }

        // ── Highlighted deviation alert points + actual travelled path ─────────
        // The red "actual path" line is only meaningful for ROUTE_DEVIATION alerts —
        // for HALT_EXCEEDED/HALT_MISSED/EXTRA_HALT the vehicle never left the assigned
        // route, so drawing it there was misleading noise, not a real deviation.
        const isRouteDeviation = (info.alertType || "").toUpperCase() === "ROUTE_DEVIATION";

        let alertPoints = [];
        let travelledCoords = [];
        if (trackWithAlertsRes?.success && trackWithAlertsRes.data) {
          const twaData = trackWithAlertsRes.data;
          if (Array.isArray(twaData.alerts)) {
            alertPoints = twaData.alerts
              .map((a) => {
                const loc = extractAlertLatLng(a);
                return loc ? { ...a, lat: loc.lat, lng: loc.lng, details: loc.details } : null;
              })
              .filter(Boolean);
          }
          if (isRouteDeviation && Array.isArray(twaData.trackingData)) {
            // trackingData isn't guaranteed to arrive in chronological order — connecting
            // it as-is produced the zig-zag/starburst pattern; sort by timestamp first.
            const sortedTrackPoints = [...twaData.trackingData]
              .sort((a, b) => trackingTimeValue(a) - trackingTimeValue(b))
              .map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude), speed: parseFloat(p.speed) || 0 }))
              .filter((c) => !isNaN(c.lat) && !isNaN(c.lng));

            if (sortedTrackPoints.length >= 2) {
              // Snap the raw GPS breadcrumb onto the actual road, same as the GPS
              // Tracking screen — otherwise straight chords between noisy fixes cut
              // across blocks instead of following the street.
              const snapped = await snapPointsToRoad(sortedTrackPoints);
              travelledCoords = snapped.map(([lat, lng]) => ({ lat, lng }));
            } else {
              travelledCoords = sortedTrackPoints.map((p) => ({ lat: p.lat, lng: p.lng }));
            }
          }
        }
        setDeviationAlerts(alertPoints);
        setActualTrackCoordinates(travelledCoords);

        fitBoundsToRoute([...assignedCoords, ...travelledCoords]);

        const geocodeTargets = [
          ...halts.map((p) => ({ id: p.id, latitude: p.latitude, longitude: p.longitude })),
          ...alertPoints.map((a) => ({ id: `deviation-${a.id}`, latitude: a.lat, longitude: a.lng })),
        ];
        if (geocodeTargets.length > 0) geocodeKeyPoints(geocodeTargets);
      } catch (err) {
        console.error("Failed to fetch deviation route:", err);
        setRouteError(err.message || "Failed to load deviation route");
        toast.error("Failed to load deviation route");
      } finally {
        setIsLoadingRoute(false);
      }
    };

    fetchDeviationRoute();
  }, [alert.vehicleTrackId, fitBoundsToRoute, geocodeKeyPoints]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    const combined = [...routeCoordinates, ...actualTrackCoordinates];
    if (combined.length > 0) fitBoundsToRoute(combined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  return (
    <div className="row" style={{ margin: 0, alignItems: "stretch" }}>
      <style>{infoWindowStyle}</style>
      {/* ── Left Panel — static ── */}
      <div className="col-lg-4" style={{ display: "flex", flexDirection: "column", padding: "14px 8px 14px 16px" }}>
        <div style={{ flex: 1, background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 3, height: 14, borderRadius: 2, background: "#0f4a45" }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.9px" }}>Route Deviation Information</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <InfoCard
              label="Vehicle Number"
              cardBg="#f0f7ff" borderColor="#99f6e4" accentColor="#0f4a45" iconBg="#ccfbf1" iconColor="#0f4a45"
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>}
            >
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>{routeInfo?.vehicleNumber || alert.vehicleNumber || "N/A"}</div>
            </InfoCard>

            <InfoCard
              label="Alert Type"
              cardBg="#fef2f2" borderColor="#fecaca" accentColor="#dc2626" iconBg="#fee2e2" iconColor="#dc2626"
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
            >
              <div style={{ fontSize: 14, fontWeight: 700, color: "#dc2626" }}>{routeInfo?.alertType || alert.notificationType || "ROUTE_ALERT"}</div>
            </InfoCard>

            <InfoCard
              label="Route Name"
              cardBg="#f0fdf4" borderColor="#bbf7d0" accentColor="#10b981" iconBg="#d1fae5" iconColor="#10b981"
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l7-7 7 7M10 21v-8h4v8"/><path d="M3 17h18"/></svg>}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", wordBreak: "break-word", lineHeight: 1.3 }}>{routeInfo?.travelRoutes?.[0]?.routeName || alert.title || "N/A"}</div>
            </InfoCard>

            <InfoCard
              label="Alert / Halt Window"
              cardBg="#f0fdfb" borderColor="#99f6e4" accentColor="#1a7a73" iconBg="#ccfbf1" iconColor="#1a7a73"
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
            >
              {routeInfo ? (
                <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Start</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{formatAlertDateTime(routeInfo.startTime)}</div>
                  </div>
                  <div style={{ width: 1, backgroundColor: "#99f6e4", height: 28, alignSelf: "center" }} />
                  <div>
                    <div style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>End</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{formatAlertDateTime(routeInfo.endTime)}</div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{formatAlertDateTime(alert.createdOn)}</div>
              )}
            </InfoCard>

            {alert.message && (
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 16px" }}>
                <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6 }}>Message</div>
                <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.5 }}>{alert.message}</div>
              </div>
            )}

            {haltPoints.length > 0 && (
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 16px" }}>
                <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>Route Waypoints ({haltPoints.length})</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 180, overflowY: "auto" }}>
                  {haltPoints.map((p, i) => (
                    <div key={p.id || i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                      <span style={{ width: 18, height: 18, borderRadius: "50%", background: HALT_POINT_COLORS[(p.pointType || "").toLowerCase()] || "#0f4a45", color: "#fff", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {waypointShortLabel(p.pointType)}
                      </span>
                      <span style={{ color: "#334155", textTransform: "capitalize" }}>{(p.pointType || "waypoint").toLowerCase()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ borderTop: "1px solid #e2e8f0", margin: "4px 0" }} />
          {isLoadingRoute ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "10px 0" }}>
              <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#1a7a73" }} />
              <span style={{ fontSize: 12, color: "#64748b" }}>Loading assigned route…</span>
            </div>
          ) : routeError ? (
            <div style={{ fontSize: 12, color: "#dc2626", textAlign: "center", padding: "10px 0" }}>⚠️ {routeError}</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "6px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 20, height: 3, borderRadius: 2, background: "#22c55e", flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: "#334155" }}>Assigned route</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 20, height: 3, borderRadius: 2, background: "#ef4444", flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: "#334155" }}>Actual travelled path</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 14, height: 14, borderRadius: "50%", background: "#dc2626", color: "#fff", fontSize: 9, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>!</span>
                <span style={{ fontSize: 12, color: "#334155" }}>Deviation alert point</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Right Panel — static map ── */}
      <div className="col-lg-8" style={{ display: "flex", flexDirection: "column", padding: "14px 16px 14px 8px" }}>
        <div ref={mapCardRef} style={{ flex: 1, borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.09)", display: "flex", flexDirection: "column", background: "#fff" }}>
          <div style={{ background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", padding: "5px 14px", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/>
              </svg>
            </div>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, flex: 1 }}>Route Map</span>
            <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
              {(LAYERS.find(l => l.id === mapType)?.label ?? mapType)} layer
            </span>
          </div>

          <div style={{ padding: "10px 14px 0" }}>
            <div style={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <div style={{ padding: "0 10px", color: "#94a3b8", display: "flex", alignItems: "center" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </div>
              <input type="text" placeholder="Search city, area, landmark or coordinates…" disabled
                style={{ flex: 1, border: "none", outline: "none", fontSize: 13, padding: "8px 4px", color: "#1e293b", background: "transparent" }} />
            </div>
          </div>

          <div style={{ flex: 1, position: "relative", margin: "10px 14px 14px" }}>
            {!isLoaded ? (
              <div className="d-flex justify-content-center align-items-center h-100">
                <div className="spinner-border" role="status" style={{ color: "#1a7a73" }}><span className="visually-hidden">Loading...</span></div>
              </div>
            ) : loadError ? (
              <div className="alert alert-danger m-3">Failed to load Google Maps.</div>
            ) : (
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={DEFAULT_CENTER}
                zoom={13}
                mapTypeId={mapType}
                onLoad={onMapLoad}
                options={{
                  mapTypeControl: false, streetViewControl: true,
                  fullscreenControl: false, zoomControl: true,
                  gestureHandling: "greedy",
                }}
              >
                {showTransit && <TransitLayer />}

                {routeCoordinates.length > 1 && (
                  <Polyline
                    path={routeCoordinates}
                    options={{ strokeColor: "#22c55e", strokeOpacity: 0.9, strokeWeight: 7, zIndex: 5 }}
                  />
                )}

                {actualTrackCoordinates.length > 1 && (
                  <Polyline
                    path={actualTrackCoordinates}
                    options={{ strokeColor: "#ef4444", strokeOpacity: 0.9, strokeWeight: 4, zIndex: 10 }}
                  />
                )}

                {haltPoints.map((p, i) => {
                  const lat = parseFloat(p.latitude);
                  const lng = parseFloat(p.longitude);
                  if (isNaN(lat) || isNaN(lng)) return null;
                  const type = (p.pointType || "").toLowerCase();
                  const title = waypointTitle(p.pointType);
                  const titleColor = HALT_POINT_COLORS[type] || "#0f4a45";
                  const markerId = p.id ?? `${type}-${i}`;
                  const rows = type === "start"
                    ? [{ label: "Start Date & Time", value: formatAlertDateTime(routeInfo?.startTime) }]
                    : type === "end"
                      ? [{ label: "End Date & Time", value: formatAlertDateTime(routeInfo?.endTime) }]
                      : [{ label: "Wait Time", value: `${p.waitTime ?? 0} minutes` }];
                  return (
                    <Marker
                      key={markerId}
                      position={{ lat, lng }}
                      icon={createCustomIcon(type)}
                      onClick={() => setActiveMarkerId(markerId)}
                    >
                      {activeMarkerId === markerId && (
                        <InfoWindow onCloseClick={() => setActiveMarkerId(null)} options={{ maxWidth: 260 }}>
                          <MarkerPopup
                            title={title}
                            titleColor={titleColor}
                            onClose={() => setActiveMarkerId(null)}
                            rows={rows}
                            address={keyPointsAddresses[p.id]}
                          />
                        </InfoWindow>
                      )}
                    </Marker>
                  );
                })}

                {/* Highlighted deviation alert points (track-with-alerts) */}
                {deviationAlerts.map((a) => {
                  const markerId = `deviation-${a.id}`;
                  return (
                    <React.Fragment key={markerId}>
                      <Circle
                        center={{ lat: a.lat, lng: a.lng }}
                        radius={60}
                        options={{
                          strokeColor: "#dc2626", strokeOpacity: 0.7, strokeWeight: 2,
                          fillColor: "#dc2626", fillOpacity: 0.15,
                        }}
                      />
                      <Marker
                        position={{ lat: a.lat, lng: a.lng }}
                        icon={buildAlertHighlightIcon()}
                        zIndex={999}
                        onClick={() => setActiveMarkerId(markerId)}
                      >
                        {activeMarkerId === markerId && (
                          <InfoWindow onCloseClick={() => setActiveMarkerId(null)} options={{ maxWidth: 280 }}>
                            <DeviationAlertPopup
                              alert={a}
                              onClose={() => setActiveMarkerId(null)}
                              address={keyPointsAddresses[markerId]}
                            />
                          </InfoWindow>
                        )}
                      </Marker>
                    </React.Fragment>
                  );
                })}
              </GoogleMap>
            )}

            {/* Control stack: Fullscreen → Layers → Transit */}
            <div style={{ position: "absolute", top: 10, right: 10, zIndex: 1000, display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit fullscreen" : "Full screen"}
                style={{
                  width: 40, height: 40, border: "none", background: "white",
                  borderRadius: 8, cursor: "pointer", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              >
                {isFullscreen ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/>
                    <path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
                    <path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
                  </svg>
                )}
              </button>
              <CustomLayerControl mapType={mapType} setMapType={setMapType} />
              <TransitToggleControl showTransit={showTransit} setShowTransit={setShowTransit} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviationRoute;
